import { Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { EMPTY, of, OperatorFunction } from 'rxjs';
import { catchError, distinctUntilKeyChanged, filter, map, skip, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { ParticipantRestService } from 'src/app/services/rest/participant-rest.service';
import { ProjectRestService } from 'src/app/services/rest/project-rest.service';
import { getActiveProjectID, getAppStateSettings, getUser } from 'src/app/store/reducers';
import * as AppStateActions from 'src/app/store/settings/actions';

@Injectable()
export class AppStateEffects {

	resetParticipantWhenProjectIdChanges$ = createEffect(() => this.store.pipe(
		select(getAppStateSettings),
		distinctUntilKeyChanged('activeProjectID'),
		skip(1), // Ignore first change from null to initial project
		map(() => AppStateActions.activeParticipantChange({ activeParticipantID: null })),
	));

	refreshActiveProjectWhenUserChanges$ = createEffect(() => this.store.pipe(
		this.userIsAuthorized(),
		select(getUser),
		distinctUntilKeyChanged('username'),
		withLatestFrom(this.store.select(getActiveProjectID)),
		switchMap(([, activeProjectID]) => {
			if (activeProjectID) {
				return this.projectRestService.listProjects$().pipe(
					catchError((error: unknown) => {
						console.log('PROJECTS LOAD ERROR: ', error);
						return of([]);
					}),
					switchMap(projects => {
						const found = projects.find(project => project.project_id == activeProjectID);
						if (found) {
							return of(AppStateActions.activeProjectChanged({ project: found }));
						} else {
							return of(AppStateActions.activeProjectChange({ activeProjectID: null }));
						}
					}),
				);
			} else {
				return EMPTY;
			}
		}),
	));

	loadProjectWhenProjectIdChanges$ = createEffect(() => this.store.pipe(
		this.userIsAuthorized(),
		select(getAppStateSettings),
		distinctUntilKeyChanged('activeProjectID'),
		switchMap(state => {
			if (state.activeProjectID) {
				return this.projectRestService.getProject$(state.activeProjectID).pipe(
					catchError((error: unknown) => {
						console.log('PROJECT LOAD ERROR: ', error);
						return EMPTY;
					}),
				);
			}
			return of(null); // bei fehlender ProjectId
		}),
		map(project => AppStateActions.activeProjectChanged({project})),
		catchError(() => of(AppStateActions.activeProjectLoadFailed())),

	));

	loadParticipantWhenParticipantIdChanges$ = createEffect(() => this.store.pipe(
		this.userIsAuthorized(),
		select(getAppStateSettings),
		switchMap(state => {
			if (state.activeProjectID && state.activeParticipantID) {
				return this.participantRestService.getParticipant$(state.activeProjectID, state.activeParticipantID).pipe(
					catchError((error: unknown) => {
						console.log('PARTICIPANT LOAD ERROR: ', error);
						return of(null);
					}),
				);
			}
			return of(null); // bei fehlender ProjectId oder ParticipantId
		}),
		map(participant => AppStateActions.activeParticipantChanged({participant})),
		catchError(() => of(AppStateActions.activeParticipantLoadFailed())),
	));

	constructor(
		private store: Store,
		private participantRestService: ParticipantRestService,
		private projectRestService: ProjectRestService,
	) {}

	/**
	 * Filtert den Stream, solange der Nutzer nicht angemeldet ist.
	 *
	 * Der Operator liefert den Eingangswert, wenn der Nutzer sich erstmalig
	 * oder erneut eingeloggt hat.
	 */
	userIsAuthorized<T>(): OperatorFunction<T, T> {
		return switchMap((state: T) => this.store.pipe(
			// User-Info aus Store laden
			select(getUser),
			// Uns interessieren nur Änderungen bei 'authorized'
			distinctUntilKeyChanged('authorized'),
			// authorized === false ignorieren
			filter(user => user.authorized),
			// Stream mit Originalwert zurückgeben
			switchMapTo(of(state)),
		));
	}
}
